import { FC, useState } from 'react'

import { useAtomValue } from 'jotai'

import {
  EmailPreview as EmailPreviewComponent,
  GreyGrey0,
  Center
} from '@sequencehq/core-components'
import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'

import { getEnvironment } from 'lib/environment/environment'
import { PdfPreviewDrawer } from 'Invoices/index'
import { useTemplateResolver } from '../../../hooks'
import Spinner from 'components/Loading'
import {
  getNotificationSenderEmail,
  useMerchantLogoUrl
} from '../../../helpers'
import {
  bccAtom,
  bodyAtom,
  ccAtom,
  customerProjectionAtom,
  invoiceIdAtom,
  invoiceNumberAtom,
  invoiceProjectionAtom,
  invoiceStatusAtom,
  subjectAtom,
  toAtom
} from './emailReview.atoms'

export const EmailPreview: FC = () => {
  const merchantSettings = useQuery(
    dashboard20240730Client.getMerchantForSequenceAccount
  )
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)

  const to = useAtomValue(toAtom)
  const cc = useAtomValue(ccAtom)
  const bcc = useAtomValue(bccAtom)
  const subject = useAtomValue(subjectAtom)
  const body = useAtomValue(bodyAtom)

  const invoiceId = useAtomValue(invoiceIdAtom)
  const invoiceNumber = useAtomValue(invoiceNumberAtom)
  const invoiceStatus = useAtomValue(invoiceStatusAtom)
  const invoice = useAtomValue(invoiceProjectionAtom)
  const customer = useAtomValue(customerProjectionAtom)

  const fromEmail = getNotificationSenderEmail(getEnvironment())
  const logoUrl = useMerchantLogoUrl()
  const { resolveTemplate } = useTemplateResolver({ invoice, customer })

  if (merchantSettings.isPending || !merchantSettings.data) {
    return (
      <Center boxSize="100%">
        <Spinner />
      </Center>
    )
  }

  const attachmentFileName = `invoice-${invoiceNumber}.pdf`

  return (
    <Center backgroundColor={GreyGrey0} boxSize="100%" padding="96px">
      <EmailPreviewComponent
        data-testid="watchtower.reviewEmail.preview"
        collapsible={false}
        maxHeight="100%"
        from={{
          name: merchantSettings.data.legalCompanyName,
          email: fromEmail,
          logoUrl
        }}
        to={to.map(resolveTemplate)}
        cc={cc.map(resolveTemplate)}
        bcc={bcc.map(resolveTemplate)}
        subject={resolveTemplate(subject)}
        body={resolveTemplate(body)}
        attachments={[
          {
            fileName: attachmentFileName,
            onClick: () => setIsModalOpen(true),
            'data-testid': 'watchtower.reviewEmail.attachment.invoicePdf'
          }
        ]}
      />
      {isModalOpen && (
        <PdfPreviewDrawer
          closeModal={() => setIsModalOpen(false)}
          invoiceId={invoiceId}
          invoiceStatus={invoiceStatus}
          invoiceNumber={invoiceNumber}
        />
      )}
    </Center>
  )
}
